import { type SyntheticEvent, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { type RootState } from 'app/store'
import {
  changeOnlineStatus,
  clearMatch,
  removePlayerFromMatch,
  setActivePlayers,
  setAdittionRet,
  setEditableTournamentFields,
  setInternalPlayers,
  setMatchDatetime,
  setMatchPoints,
  setSelectionParameters,
  takeSelectedCard,
  updateSelectedCard,
  updateTeam
} from 'app/store/slices/editableTournament'
import { type TPlayer } from 'pages/PlayersPage/types'
import { type TMatch } from 'pages/TournamentsPage/types'
import { userIsOperator } from 'shared/checkUser'
import { FINAL_ID, HEMIFINAL_ID, FOR_SEVENTH_PLACE, FOR_FIFTH_PLACE, } from 'shared/constants'
import ChangeTime from 'icons/time-change.png'
import DeleteIcon from 'icons/remove.svg'
import { TeamLine } from './components/TeamLine'
import { IntermediateLine } from './components/IntermediateLine'
import styles from './styles.module.css'
import { MatchEditor } from 'widgets/match-editor'
import { PlayerList } from 'pages/TournamentsPageN/ui/player-list'

interface TTeamNames {
  flag: string
  name: string
}

export interface TTeamScore {
  score: number | undefined
  tiebreak?: number | undefined
}

interface ITournamentCardArgs {
  match: TMatch
  selectable?: boolean
  selectedMatchNumber?: number
  cardId: number
  tourId: number
  isTopGrid?: boolean,
  isBottomGrid?: boolean,
}

type TSelectableParameters = {
  tourId: number | undefined, 
  cardId: number | undefined, 
  teamId: number | undefined, 
  isTopGrid?: boolean,
  isBottomGrid?: boolean
}

export function RemoveButton({onRemove, className}: any){
  return <img src={DeleteIcon} className={styles.RemoveIcon + ' ' + className } onClick={onRemove}/>
}

export function TournamentCard ({
  match,
  selectable,
  cardId,
  tourId,
  selectedMatchNumber,
  isTopGrid = false,
  isBottomGrid = false,
}: ITournamentCardArgs) {
  const [operatorId, setOperatorId] = useState('')
  const [matchId, setMatchId] = useState('')
  const [changeStartMatch, setСhangeStartMatch] = useState('')
  const [changeStartMatchStatus, setСhangeStartMatchStatus] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [resultMessage, setResultMessage] = useState('')
  const [schedulerIsOpen, setSchedulerIsOpen] = useState(false)
  const [isEditor, setIsEditor] = useState(false)
  const [addPlayer, setAddPlayer] = useState<undefined|0|1>(undefined)

  const isEllimitationTournament = useSelector((state: RootState) => state.editableTournamentSlice.isEllimination)
  const typeTournament = useSelector((state: RootState) => state.editableTournamentSlice.type)
  const selectableParameters: TSelectableParameters = useSelector((state: RootState) => state.editableTournamentSlice.selectableParameters)
  const activePlayers: TPlayer[] = useSelector((state: RootState) => state.editableTournamentSlice.activePlayers)
  const internalPlayers: TPlayer[] = useSelector((state: RootState) => state.editableTournamentSlice.internalPlayers)
  const editableTournament: any = useSelector((state: RootState) => state.editableTournamentSlice)
  const dispatch = useDispatch()

  const selected = (() => {
    if(isEllimitationTournament){
      return selectableParameters.tourId === tourId && selectableParameters.cardId === cardId
    }

    if(selectableParameters.cardId === FINAL_ID && cardId === FINAL_ID){
      return true
    }

    if(selectableParameters.cardId === HEMIFINAL_ID && cardId === HEMIFINAL_ID){
      return true
    }

    if(selectableParameters.cardId === FOR_SEVENTH_PLACE && cardId === FOR_SEVENTH_PLACE){
      return true
    }

    if(selectableParameters.cardId === FOR_FIFTH_PLACE && cardId === FOR_FIFTH_PLACE){
      return true
    }

    return selectableParameters.tourId === tourId &&
      selectableParameters.cardId === cardId &&
      ((selectableParameters.isTopGrid && isTopGrid) ||  
      (selectableParameters.isBottomGrid && isBottomGrid))
  })()

  const { team1Score, team2Score, team1Names, team2Names } = match

  /**
   * Online indicator
   */

  const switchCardOnlineStatement = (e: SyntheticEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(changeOnlineStatus({
      match,
      cardId,
      tourId,
      isTopGrid,
      isBottomGrid,
      isEllimination: isEllimitationTournament,
    } as any))
  }

  //////////////////////////////////////////

  const onTopTeamLineClick = useCallback((e: SyntheticEvent<HTMLDivElement, Event>) => {
    dispatch(setSelectionParameters({
      tourId, cardId, teamId: 0, 
      isTopGrid,
      isBottomGrid,
      isEllimination: isEllimitationTournament,
    }))
    e.stopPropagation()
    setAddPlayer(0)
  }, [tourId, cardId])

  const onBottomTeamLineClick = useCallback((e: SyntheticEvent<HTMLDivElement, Event>) => {
    dispatch(setSelectionParameters({
      tourId, cardId, teamId: 1, 
      isTopGrid,
      isBottomGrid,
      isEllimination: isEllimitationTournament,
    }))
    e.stopPropagation()
    setAddPlayer(1)
  }, [tourId, cardId])

  const setDatetime = (e: any) => dispatch(setMatchDatetime({
    datetime: new Date(e.target.value).getTime(), // getGMTDate(e.target.value),
    tourId,
    cardId,
    isTopGrid,
    isBottomGrid,
    isEllimination: isEllimitationTournament,
  }))

  const setPoints = (matchbeats: any[]) => dispatch(setMatchPoints({
    matchbeats: matchbeats,
    tourId,
    cardId,
    isTopGrid,
    isBottomGrid,
    isEllimination: isEllimitationTournament,
  }))

  const setAdditionRet = (numberTeam: number) => dispatch(setAdittionRet({
    teamId: numberTeam,
    tourId,
    cardId,
    isTopGrid,
    isBottomGrid,
    isEllimination: isEllimitationTournament,
  }))

  const operatorCardSelection = (e: SyntheticEvent) => dispatch(setSelectionParameters({
    tourId, cardId, teamId: undefined, 
    isTopGrid,
    isBottomGrid,
    isEllimination: isEllimitationTournament,
  }))

  const clearCard = () => dispatch(clearMatch({
    tourId,
    cardId,
    isTopGrid,
    isBottomGrid,
    isEllimination: isEllimitationTournament,
  }))

  const onPlayerRemovingRequest = (removingPlayer: TPlayer) => {
    const playerInActive = activePlayers.findIndex((p: TPlayer) => p.id === removingPlayer.id)
    
    dispatch(removePlayerFromMatch({
      playerId: removingPlayer.id,
      cardId,
      tourId,
      isTopGrid,
      isBottomGrid,
      isEllimination: isEllimitationTournament,
    }))
    

    if(playerInActive === -1){
      return
    }

    dispatch(setActivePlayers(activePlayers.filter((p: TPlayer) => p.id !== removingPlayer.id)))
    dispatch(setInternalPlayers([...internalPlayers, removingPlayer]))
  }

  const onUpdateOperatorId = async () => {
    const card = JSON.parse(JSON.stringify(takeSelectedCard(editableTournament, editableTournament.selectableParameters)))
    card.operatorId = Number(operatorId)
    const newEditableTournament = updateSelectedCard(editableTournament, card, editableTournament.selectableParameters)

    dispatch(setEditableTournamentFields(newEditableTournament))
    setResultMessage(`ID оператора добавлен`)
    setTimeout(() => setResultMessage(''), 5000)
    setOperatorId('')
  }

  const onUpdateMatchId = () => {
    const card = JSON.parse(JSON.stringify(takeSelectedCard(editableTournament, editableTournament.selectableParameters)))
    card.hash = Number(matchId)
    const newEditableTournament = updateSelectedCard(editableTournament, card, editableTournament.selectableParameters, false)

    dispatch(setEditableTournamentFields(newEditableTournament))
    setResultMessage(`ID матча добавлен`)
    setTimeout(() => setResultMessage(''), 5000)
    setMatchId('')
  }

  const onUpdateChangeStartMatch = () => {
    const card = JSON.parse(JSON.stringify(takeSelectedCard(editableTournament, editableTournament.selectableParameters)))
    card.changeStartMatch = changeStartMatch
    const newEditableTournament = updateSelectedCard(editableTournament, card, editableTournament.selectableParameters)

    dispatch(setEditableTournamentFields(newEditableTournament))
    setResultMessage(`Информация о переносе матча добавлена`)
    setTimeout(() => setResultMessage(''), 5000)
    setСhangeStartMatch('')
  }

  const setAdditionPlayer = (playerIndex: number, addition: string) => {
    const card = JSON.parse(JSON.stringify(takeSelectedCard(editableTournament, editableTournament.selectableParameters)))
    if (playerIndex === 0) {
      card.team1Addition[0] === "ret" || "cup" ? card.team1Addition.unshift(addition) : card.team1Addition[0] = addition
    }
    if (playerIndex === 1) {
      card.team2Addition[0] === "ret" || "cup" ? card.team2Addition.unshift(addition) : card.team2Addition[0] = addition
    }
    const newEditableTournament = updateSelectedCard(editableTournament, card, editableTournament.selectableParameters)

    dispatch(setEditableTournamentFields(newEditableTournament))
    setResultMessage(`${addition} выставлен игроку № ${playerIndex + 1}`)
    setTimeout(() => setResultMessage(''), 5000)
  }

  const formatDate = (date: Date) => date.toLocaleString()
  const manualGridVariants: string[] = ['', 'WC', 'LL', 'Q', '1', '2', '3', '4']

  const appendPlayerToMatch = (playerId: string, teamId: number) => {
    console.debug('playerId: ', playerId)
    const choosenPlayerId = internalPlayers.findIndex((p: TPlayer) => ((p._id !== undefined && playerId === p._id) || (p.id !== undefined && playerId === p.id)))
    const targetPlayer = internalPlayers[choosenPlayerId]

    /**
     * Проверяем что индексы выделения вообще существуют
     */

    if(tourId === undefined || cardId === undefined || teamId === undefined){
        return
    }

    /**
     * Проверяем что и правда выбрана хоть какая-то карточка
     */
    if(tourId === -1 || teamId === -1){
        return
    }

    if (teamId !== 0 && teamId !== 1) {
        return
    }

    // /**
    //  * Проверяем что вообще существует турнир
    //  */
    // if(!tours){
    //     return
    // }

    dispatch(updateTeam({
        player: targetPlayer,
        teamId: teamId,
    }))
  }

  return (
    <div 
      className={styles.containerCart}
      onClick={operatorCardSelection}
      onMouseEnter={operatorCardSelection}
      onDoubleClick={() => setIsEditor(true)}
    >
      {isEditor && <MatchEditor onClose={() => setIsEditor(false)} matchData={match} setPoints={setPoints}/>}
      {addPlayer !== undefined && <div className={styles.PlayerList} onMouseLeave={() => setAddPlayer(undefined)}>
        <div onClick={() => setAddPlayer(undefined)}>Закрыть</div>
        <PlayerList players={internalPlayers} onAddClick={(playerId) => appendPlayerToMatch(playerId, addPlayer)}/>
      </div>}
      <div className={`${styles.TournamentGreedCard} ${selectable && styles.Selectable} ${selected && styles.Selected}`}>
        <RemoveButton onRemove={clearCard} className={styles.DropIcon}/>
        <div className={styles.SheduleMark}>
          <input className={styles.DateTime} type='datetime-local' onChange={setDatetime} />
          <img className={match.changeStartMatch ? styles.ChangeTime + ' ' + styles.ChangeTimeActive : styles.ChangeTime} alt={''} src={ChangeTime} onClick={() => setСhangeStartMatchStatus(!changeStartMatchStatus)}/>  
        </div>
        <div className={styles.OnlineIndicator} onClick={switchCardOnlineStatement}>
          {formatDate(new Date(parseInt(match.datetime || '0'))) || ''}
        </div>
        {/* Name1Playe + Score */}
        <TeamLine
          selected={selected || false}
          teamSelected={(selected && selectableParameters.teamId === 0) as boolean}
          onTeamLineClick={onTopTeamLineClick}
          team={team1Names}
          onPlayerDblClick={onPlayerRemovingRequest}
          teamScore={team1Score}
          addition={match.team1Addition}
          onRetClick={() => setAdditionRet(0)}
        />
        {/* Sets */}
        <IntermediateLine
          scoreLength={team1Score?.length || 1}
          selected={selected || false}
          selectedMatchNumber={selectedMatchNumber}
        />
        {/* Name2Playe + Score */}
        <TeamLine
          selected={selected || false}
          teamSelected={(selected && selectableParameters.teamId === 1) as boolean}
          onTeamLineClick={onBottomTeamLineClick}
          team={team2Names}
          onPlayerDblClick={onPlayerRemovingRequest}
          teamScore={team2Score}
          addition={match.team2Addition}
          onRetClick={() => setAdditionRet(1)}
        />
        <div>
          <div className={styles.operatorId}>
            {match.operatorId ? 'Id оператора: ' + match.operatorId : 'Id оператора: '}
          </div>
          <div className={styles.matchId}>
            {match.hash ? 'Id матча: ' + match.hash : 'Id матча: '}
          </div> 
        </div>
      </div>
      {schedulerIsOpen && <div className={styles.OpenScheduler}>
        <input className={styles.DateTime} type='datetime-local' onChange={setDatetime} />  
      </div>}
      {selected && <div className={styles.InputsId}>
        <div>
          <input onChange={(event) => { setOperatorId(event.target.value) }} placeholder="Введите ID оператора"/>
          <button onClick={ onUpdateOperatorId }>{match.operatorId && !operatorId ? 'Очистить' : 'Добавить'}</button>
        </div>
        <div>
          <input onChange={(event) => { setMatchId(event.target.value) }} placeholder="Введите ID матча"/>
          <button onClick={ onUpdateMatchId }>{match.hash && !matchId ? 'Очистить' : 'Добавить'}</button>
        </div>
      </div>}
      {selected && changeStartMatchStatus && <div className={styles.ChangeStartMatch}>
        <textarea onChange={(event) => { setСhangeStartMatch(event.target.value) }} placeholder={ match.changeStartMatch || "Матч перенесен на **:**:**/2 часа"}/>
        <button onClick={ onUpdateChangeStartMatch }>{match.changeStartMatch && !changeStartMatch ? 'Очистить' : 'Добавить'}</button>
      </div>}
      <div className={styles.InputsPlayers}>
        <Select
          labelId="demo-simple-select-helper-label"
          id="demo-simple-select-helper"
          value={match.team1Addition[0]}
          label="Age"
          onChange={(event: SelectChangeEvent) => {
            setAdditionPlayer(0, event.target.value)
          }}
        >
          {manualGridVariants.map((val: string) =>  <MenuItem value={val}>{val}</MenuItem>  )}
        </Select>
        <Select
          labelId="demo-simple-select-helper-label"
          id="demo-simple-select-helper"
          value={match.team2Addition[0]}
          label="Age"
          onChange={(event: SelectChangeEvent) => {
            setAdditionPlayer(1, event.target.value)
          }}
        >
          {manualGridVariants.map((val: string) =>  <MenuItem value={val}>{val}</MenuItem>  )}
        </Select>
      </div>
      <div className={styles.responseMessage}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {resultMessage && <Alert severity="success">{resultMessage}</Alert>}
      </div>
    </div>
  )
}
